import { Product } from "data/data";

/**
 * Check if a product has any modifier sets that require selection.
 */
export const hasRequiredModifiers = (product: Product): boolean => {
  if (!product.modifierSets || product.modifierSets.length === 0) return false;
  return product.modifierSets.some((ms) => ms.noOfRequired > 0);
};

/**
 * Check if a product has any modifier sets at all.
 */
export const hasModifiers = (product: Product): boolean => {
  return !!product.modifierSets && product.modifierSets.length > 0;
};

/**
 * Calculate the total additional price from selected modifiers.
 */
export const getModifierPrice = (
  product: Product,
  modifierState: Record<number, Record<number, boolean>>
): number => {
  let total = 0;
  if (!product.modifierSets) return total;

  product.modifierSets.forEach((modifierSet) => {
    const setSelections = modifierState[modifierSet.id];
    if (!setSelections) return;

    modifierSet.modifierSetModifierItems?.forEach((modifierItem) => {
      if (setSelections[modifierItem.id]) {
        total += modifierItem.modifierItem.onlinePrice;
      }
    });
  });

  return total;
};

interface ModifierValidationResult {
  valid: boolean;
  errors: Array<{
    modifierSetId: number;
    modifierSetName: string;
    message: string;
  }>;
}

/**
 * Validate that all required modifier sets have valid selections.
 */
export const validateModifiers = (
  product: Product,
  modifierState: Record<number, Record<number, boolean>>
): ModifierValidationResult => {
  const errors: ModifierValidationResult["errors"] = [];

  if (!product.modifierSets) return { valid: true, errors };

  product.modifierSets.forEach((modifierSet) => {
    const setSelections = modifierState[modifierSet.id] || {};
    const selectedCount = Object.values(setSelections).filter(Boolean).length;

    if (selectedCount < modifierSet.noOfRequired) {
      errors.push({
        modifierSetId: modifierSet.id,
        modifierSetName: modifierSet.name,
        message: `Please select at least ${modifierSet.noOfRequired} modifier${
          modifierSet.noOfRequired > 1 ? "s" : ""
        }.`,
      });
    }

    if (modifierSet.maximum && selectedCount > modifierSet.maximum) {
      errors.push({
        modifierSetId: modifierSet.id,
        modifierSetName: modifierSet.name,
        message: `Please select at most ${modifierSet.maximum} modifiers.`,
      });
    }
  });

  return { valid: errors.length === 0, errors };
};

/**
 * Get the list of selected modifier item names for display (e.g., in cart).
 */
export const getSelectedModifierNames = (
  product: Product,
  modifierState: Record<number, Record<number, boolean>>
): string[] => {
  const names: string[] = [];
  if (!product.modifierSets) return names;

  product.modifierSets.forEach((modifierSet) => {
    const setSelections = modifierState[modifierSet.id];
    if (!setSelections) return;

    modifierSet.modifierSetModifierItems?.forEach((modifierItem) => {
      if (setSelections[modifierItem.id]) {
        names.push(modifierItem.modifierItem.name);
      }
    });
  });

  return names;
};

// --- Nested modifier support (child-modifier-sets tree stored on cart item as
// `nestedModifierSelections`, keyed by top-level modifier-item id). Uses `any`
// so this file stays free of tight coupling to the NestedModifierSection types.

export interface ModifierChip {
  name: string;
  depth: number; // 1 = top-level, 2 = child, 3 = grandchild, ...
  price?: number;
}

/** Walk a nested bundle DFS and collect { name, depth, price } for every selected item. */
function traceBundleSegments(bundle: any, depth: number): ModifierChip[] {
  if (!bundle || !bundle.fetched) return [];
  const segments: ModifierChip[] = [];
  for (const set of bundle.fetched) {
    const sel = bundle.selections?.[set.id] ?? {};
    const selectedIds = Object.keys(sel)
      .filter((k) => sel[Number(k)])
      .map(Number);
    for (const id of selectedIds) {
      const found = set.items?.find((x: any) => x.id === id);
      if (found) {
        segments.push({
          name: found.name,
          depth,
          price: found.onlinePrice ?? 0,
        });
      }
      const child = bundle.children?.[id];
      if (child) segments.push(...traceBundleSegments(child, depth + 1));
    }
  }
  return segments;
}

/** Sum onlinePrice of every selected item in a nested bundle tree. */
function sumBundlePrice(bundle: any): number {
  if (!bundle || !bundle.fetched) return 0;
  let total = 0;
  for (const set of bundle.fetched) {
    const sel = bundle.selections?.[set.id] ?? {};
    for (const item of set.items ?? []) {
      if (sel[item.id]) total += item.onlinePrice || 0;
    }
  }
  for (const child of Object.values(bundle.children ?? {})) {
    total += sumBundlePrice(child);
  }
  return total;
}

/** Total upcharge from every nested modifier selection on this cart item. */
export const getNestedModifierPrice = (item: any): number => {
  const nested = item?.nestedModifierSelections;
  if (!nested) return 0;
  return Object.values(nested).reduce<number>(
    (acc, bundle) => acc + sumBundlePrice(bundle),
    0
  );
};

/**
 * Cart display: split top-level modifier selections into flat pills vs breadcrumb
 * chains (for items that had nested selections). Each breadcrumb segment carries
 * its own depth (1 = top, 2 = child, 3 = grandchild) so consumers can render each
 * level in a distinct color.
 */
export const getSelectedModifierDisplay = (
  item: any
): { plain: ModifierChip[]; breadcrumbs: ModifierChip[][] } => {
  const plain: ModifierChip[] = [];
  const breadcrumbs: ModifierChip[][] = [];
  const modifierSets = item?.modifierSets ?? [];
  const modifierChecked = item?.modifierChecked ?? {};
  const nested = item?.nestedModifierSelections ?? {};

  for (const set of modifierSets) {
    const sel = modifierChecked[set.id] ?? {};
    for (const link of set.modifierSetModifierItems ?? []) {
      if (!sel[link.id]) continue;
      const modItemId = link.modifierItem?.id;
      const modItemName = link.modifierItem?.name ?? "";
      const modItemPrice = link.modifierItem?.onlinePrice ?? 0;
      const bundle = modItemId != null ? nested[modItemId] : undefined;
      if (bundle) {
        breadcrumbs.push([
          { name: modItemName, depth: 1, price: modItemPrice },
          ...traceBundleSegments(bundle, 2),
        ]);
      } else {
        plain.push({ name: modItemName, depth: 1, price: modItemPrice });
      }
    }
  }
  return { plain, breadcrumbs };
};

// Backend order-payload shape for a selected modifier item. When an item has
// nested selections, `childModifierSetItemsList` recurses with the same shape.
export interface OrderModifierItem {
  modifierItemId: number;
  modifierSetId: number;
  quantity: number;
  childModifierSetItemsList?: OrderModifierItem[];
}

/** Recursively serialize a nested bundle into the backend's order-payload shape. */
function bundleToOrderList(bundle: any): OrderModifierItem[] {
  const out: OrderModifierItem[] = [];
  if (!bundle || !bundle.fetched) return out;
  for (const set of bundle.fetched) {
    const sel = bundle.selections?.[set.id] ?? {};
    const selectedIds = Object.keys(sel)
      .filter((k) => sel[Number(k)])
      .map(Number);
    for (const id of selectedIds) {
      const entry: OrderModifierItem = {
        modifierItemId: id,
        modifierSetId: set.id,
        quantity: 1,
      };
      const childBundle = bundle.children?.[id];
      if (childBundle) {
        const childList = bundleToOrderList(childBundle);
        if (childList.length > 0) entry.childModifierSetItemsList = childList;
      }
      out.push(entry);
    }
  }
  return out;
}

/**
 * Build the `modifierSetItemsList` for the order-placement payload from a cart
 * item's top-level `modifierChecked` and its `nestedModifierSelections` tree.
 *
 * Emits entries shaped like:
 *   { modifierItemId, modifierSetId, quantity,
 *     childModifierSetItemsList?: [...same shape recursively] }
 */
export const buildOrderModifierItems = (item: any): OrderModifierItem[] => {
  const out: OrderModifierItem[] = [];
  const modifierSets = item?.modifierSets ?? [];
  const modifierChecked = item?.modifierChecked ?? {};
  const nested = item?.nestedModifierSelections ?? {};

  for (const set of modifierSets) {
    const sel = modifierChecked[set.id] ?? {};
    for (const link of set.modifierSetModifierItems ?? []) {
      if (!sel[link.id]) continue;
      const modItemId = link.modifierItem?.id;
      if (modItemId == null) continue;
      const entry: OrderModifierItem = {
        modifierItemId: modItemId,
        modifierSetId: set.id,
        quantity: 1,
      };
      const bundle = nested[modItemId];
      if (bundle) {
        const childList = bundleToOrderList(bundle);
        if (childList.length > 0) entry.childModifierSetItemsList = childList;
      }
      out.push(entry);
    }
  }
  return out;
};

/**
 * Text color per modifier nesting depth. Matches the customization sheet's
 * left-border colors so users can associate cart chips with the level they
 * picked them at.
 */
export const modifierDepthColorClass = (depth: number): string => {
  switch (depth) {
    case 1:
      return "text-blue-600 dark:text-blue-400";
    case 2:
      return "text-purple-600 dark:text-purple-400";
    case 3:
      return "text-emerald-600 dark:text-emerald-400";
    default:
      return "text-amber-600 dark:text-amber-400";
  }
};
